/**
 * TA Cockpit → Tracking — the shared small parts.
 *
 * ⚠️ EVERY KPI CARRIES ITS DEFINITION. Each figure on this tab used to be a cell
 * on a Google Sheet whose rule lived in somebody's head, and three of the Hiring
 * demand board's KPIs turned out to be measuring the wrong thing until they were
 * checked against the HDB. So the `i` is not decoration: a KPI nobody can define
 * is one nobody should act on. Same control as `demand/SummaryStrip.tsx`, for the
 * same reason and with the same keyboard and touch behaviour.
 */
import { useState } from 'react';

export type Tone = 'neutral' | 'ok' | 'warn';

/**
 * The definition, on a small `i`.
 *
 * A real `<button>` with real state, not a `title` attribute: `title` shows on
 * hover with a mouse and NOWHERE else — not on keyboard focus, not on a tap — so
 * from a keyboard or a touch screen the definitions would simply not exist.
 *
 * ⚠️ CLICK OPENS, it does not toggle. A toggle here is self-defeating: `focus`
 * and `mouseenter` both fire BEFORE `click`, so by the time the toggle runs the
 * tooltip is already open and the click shuts it again — the one interaction
 * this control exists to support was the one that did nothing. Closing is left
 * to blur and mouse-leave, which is what a tap elsewhere does anyway.
 */
export function WhatThisCounts({ label, why }: { label: string; why: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-expanded={open}
        aria-label={`What "${label}" counts`}
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="grid h-3.5 w-3.5 shrink-0 place-items-center rounded-full border border-gray-300 text-[9px] font-bold normal-case leading-none text-gray-400 hover:border-gray-500 hover:text-gray-600 focus:border-gray-500 focus:outline-none"
      >
        i
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-1/2 top-5 z-20 w-64 -translate-x-1/2 rounded-md border border-gray-200 bg-white p-2 text-[11px] font-normal normal-case leading-snug tracking-normal text-gray-600 shadow-lg"
        >
          {why}
        </span>
      )}
    </span>
  );
}

/**
 * The label sits ABOVE the value and the tiles are a fixed row.
 *
 * Same constraint the Role Prioritisation strip records: an inline
 * "label value note" row wraps onto a second line at real widths, which defeats
 * the one thing a KPI strip exists for.
 */
export function KpiTile({
  label,
  value,
  note,
  why,
  tone = 'neutral',
  testId,
}: {
  label: string;
  value: number | string;
  note?: string;
  why: string;
  tone?: Tone;
  testId?: string;
}) {
  const valueTone =
    tone === 'warn' ? 'text-amber-700' : tone === 'ok' ? 'text-emerald-700' : 'text-gray-900';
  return (
    <div
      data-testid={testId}
      className="min-w-[152px] flex-1 border-r border-gray-100 p-3.5 last:border-r-0"
    >
      <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        {label}
        <WhatThisCounts label={label} why={why} />
      </div>
      <div className={`mt-0.5 text-2xl font-semibold tabular-nums ${valueTone}`}>{value}</div>
      {note && <div className="text-xs text-gray-500">{note}</div>}
    </div>
  );
}

export function Section({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</h2>
        {aside && <div className="text-xs text-gray-500">{aside}</div>}
      </div>
      {children}
    </section>
  );
}

/**
 * A progress bar with the expected-pace marker on it.
 *
 * The marker is where the CALENDAR says the team should be by now, so "62% of
 * target" can be read as ahead or behind without doing the arithmetic. It
 * carries no label: at card width a figure there overlaps the caption, and the
 * caption states the pace outright anyway.
 */
export function ProgressBar({
  pct,
  markerPct,
  tone,
}: {
  pct: number;
  markerPct?: number;
  tone: Tone;
}) {
  const fill = tone === 'warn' ? 'bg-amber-500' : tone === 'ok' ? 'bg-emerald-500' : 'bg-gray-400';
  return (
    <div className="relative mt-1.5 h-2 w-full overflow-hidden rounded bg-gray-100">
      <div className={`h-full ${fill}`} style={{ width: `${clamp(pct) * 100}%` }} />
      {markerPct !== undefined && (
        <div
          aria-hidden
          className="absolute top-0 h-full w-px bg-gray-700"
          style={{ left: `${clamp(markerPct) * 100}%` }}
        />
      )}
    </div>
  );
}

export const clamp = (v: number) => Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0));

/** For BARS, which cannot draw past their own width. */
export const pct = (v: number) => `${Math.round(clamp(v) * 100)}%`;

/**
 * For FIGURES. Deliberately unclamped: Hired counts offers accepted this
 * trimester and Target counts the seats it planned, so hired ÷ target can
 * legitimately pass 1 — and 125% read as 100% would hide the one result worth
 * shouting about.
 */
export const pctRaw = (v: number) =>
  `${Math.round((Number.isFinite(v) ? Math.max(0, v) : 0) * 100)}%`;

/** "1 role" / "4 roles" — a count that reads as a sentence. */
export const roleWord = (n: number) => `${n} ${n === 1 ? 'role' : 'roles'}`;
